/* components/kitchen/IngredientsTable.jsx — таблица состава ТТК.
   md+ : полный стол 6 колонок (Ингредиент / Брутто / Нетто / Выход / Ед. / %хол.·%теп.).
   xs/sm: карточки БЕЗ свертывания — все поля видны сразу (паритет с десктопом):
          шапка = имя+привязка+действия; тело = замеры+ед. в одну строку + derived-%.
   Derived-колонки %хол./%теп. — НЕредактируемы, визуально отличны (.derived-cell).
   При scale≠1 граммы показываются масштабно и поля только для чтения (превью нормы).
   Пропсы: rows, onRowChange(id,patch), onDelete(id), onRebind(id,entity|null),
           onOpenTarget(row), scale, readOnly.
   ХЕНДОФФ → frontend/src/components/ttk/IngredientsTable.jsx. */

import React from 'react';
import { IngredientPicker } from './IngredientPickers.jsx';
import { IngredientName } from '../ttk/TTKViewBlocks.jsx';
import { RecipeBindingBadge } from '../ui/Badge.jsx';
import { IconButton } from '../ui/Buttons.jsx';
import { NativeSelect, NumberInput } from '../ui/Field.jsx';
import { Icon } from '../ui/Icon.jsx';
import { hooks } from '../../hooks/index.jsx';

  const UNITS = ['г', 'кг', 'мл', 'л', 'шт'];
  const g1 = (v) => (v == null || v === '' || isNaN(v) ? '—' : new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(Number(v)));

  // %теп. derived: обычно потери (≥0). Отрицательное значение = ПРИВАР —
  // масса выросла при варке (рис/паста/крупы впитали воду). Показываем его как
  // «+N привар», а не голым минусом под колонкой «потери».
  function HotLoss({ v }) {
    if (v == null || v === '' || isNaN(v)) return '—';
    const n = Number(v);
    if (n < 0) return <span className="ing-privar" title="Привар — масса выросла при варке (впитывание воды)">+{g1(-n)} привар</span>;
    return g1(n);
  }

  // компактное числовое поле для ячейки таблицы (без степперов — экономит ширину)
  function CellNum({ value, onChange, readOnly }) {
    if (readOnly) return <span className="num cell-num cell-num--ro">{g1(value)}</span>;
    return (
      <input type="text" inputMode="decimal" className="cell-num num" value={value ?? ''}
        onChange={(e) => onChange(e.target.value.replace(/[^\d.,]/g, '').replace(',', '.'))}
        onBlur={(e) => onChange(e.target.value === '' ? null : Number(e.target.value.replace(',', '.')))} />
    );
  }

  // Ключ строки для onRowChange/onDelete/onRebind: редактор ТТК сопоставляет
  // строки по КЛИЕНТСКОМУ `_rid` (стабилен и для новых, и для сохранённых строк).
  // Раньше отдавали `r.id` (БД-id) — он не совпадал с `_rid`, и
  // редактирование/удаление/перепривязка сохранённых строк молча не срабатывали.
  // Фолбэк на id/индекс — для read-only потребителей без `_rid` (TTKCanvas).
  function rid(r, i) { return r._rid != null ? r._rid : (r.id != null ? r.id : i); }

  /* ── desktop ── */
  function DesktopTable({ rows, onRowChange, onDelete, onRebind, onOpenTarget, scale, readOnly, showBinding }) {
    const sc = Number(scale) || 1;
    const scaled = sc !== 1;
    const ro = readOnly || scaled;
    const dispG = (v) => (v == null ? null : v * sc);
    const bindState = (r) => (r.target_kind === 'card' || r.target_kind === 'ttk') ? 'bound' : 'unbound';
    return (
      <div className="ing-table-wrap">
        <table className="ing-table">
          <thead>
            <tr>
              <th className="ing-table__c-name">Ингредиент</th>
              <th className="ing-table__c-num">Брутто, г</th>
              <th className="ing-table__c-num">Нетто, г</th>
              <th className="ing-table__c-num">Выход, г</th>
              <th className="ing-table__c-unit">Ед.</th>
              <th className="ing-table__c-derived num" title="Потери холодной обработки">% хол.</th>
              <th className="ing-table__c-derived num" title="Потери / привар тепловой обработки">% теп.</th>
              <th className="ing-table__c-act" aria-label="Действия" />
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              return (
                <tr key={rid(r, i)}>
                  <td className="ing-table__c-name">
                    <IngredientName row={r} onOpenTarget={onOpenTarget}
                      onNameChange={ro ? undefined : (v) => onRowChange(rid(r, i), { name_raw: v })} />
                    {showBinding && <RecipeBindingBadge state={bindState(r)} readOnly className="ing-table__bind" />}
                  </td>
                  <td className="ing-table__c-num"><CellNum value={scaled ? dispG(r.amount_brutto_g) : r.amount_brutto_g} readOnly={ro} onChange={(v) => onRowChange(rid(r, i), { amount_brutto_g: v })} /></td>
                  <td className="ing-table__c-num"><CellNum value={scaled ? dispG(r.amount_netto_g) : r.amount_netto_g} readOnly={ro} onChange={(v) => onRowChange(rid(r, i), { amount_netto_g: v })} /></td>
                  <td className="ing-table__c-num"><CellNum value={scaled ? dispG(r.weight_in_dish_g) : r.weight_in_dish_g} readOnly={ro} onChange={(v) => onRowChange(rid(r, i), { weight_in_dish_g: v })} /></td>
                  <td className="ing-table__c-unit">
                    {ro ? <span className="num cell-num--ro">{r.unit || 'г'}</span>
                      : <NativeSelect value={r.unit || 'г'} options={UNITS} onChange={(e) => onRowChange(rid(r, i), { unit: e.target.value })} className="cell-unit" />}
                  </td>
                  <td className="ing-table__c-derived num derived-cell">{r.cold_loss_pct == null ? '—' : g1(r.cold_loss_pct)}</td>
                  <td className="ing-table__c-derived num derived-cell"><HotLoss v={r.hot_loss_pct} /></td>
                  <td className="ing-table__c-act">
                    {!readOnly && <IconButton icon="trash" size="sm" variant="ghost" tooltip="Удалить строку" onClick={() => onDelete(rid(r, i))} />}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {scaled && <p className="ing-table__scale-note t-caption text-dim"><Icon name="info" size={13} /> Показан масштаб ×{sc}. Снимите масштаб (×1), чтобы редактировать.</p>}
      </div>
    );
  }

  /* ── mobile cards: без свертывания — все поля видны сразу ──
     Имя ведёт себя как на десктопе (привязанное — тап открывает цель; непривязанное
     при редактировании — инлайн-инпут). Кнопка-иконка «открыть» в шапке дублирует
     тап по имени: на тач-экранах нет hover-подсказки, что имя кликабельно. */
  function MobileCards({ rows, onRowChange, onDelete, onRebind, onOpenTarget, scale, readOnly, showBinding }) {
    const sc = Number(scale) || 1;
    const scaled = sc !== 1;
    const ro = readOnly || scaled;
    const dispG = (v) => (v == null ? null : v * sc);
    const bindState = (r) => (r.target_kind === 'card' || r.target_kind === 'ttk') ? 'bound' : 'unbound';
    return (
      <div className="ing-cards">
        {rows.map((r, i) => {
          const id = rid(r, i);
          const bound = r.target_kind === 'card' || r.target_kind === 'ttk';
          return (
            <div className="ing-card" key={id}>
              <div className="ing-card__head">
                <div className="ing-card__name grow">
                  <IngredientName row={r} onOpenTarget={onOpenTarget}
                    onNameChange={ro ? undefined : (v) => onRowChange(id, { name_raw: v })} />
                  {showBinding && <RecipeBindingBadge state={bindState(r)} readOnly className="ing-card__bind" />}
                </div>
                {bound && typeof onOpenTarget === 'function' && (
                  <IconButton icon="external-link" size="sm" variant="ghost"
                    tooltip={'Открыть ' + (r.target_kind === 'ttk' ? 'полуфабрикат' : 'карточку')}
                    onClick={() => onOpenTarget(r)} />
                )}
                {!readOnly && <IconButton icon="trash" size="sm" variant="ghost" tooltip="Удалить строку" onClick={() => onDelete(id)} />}
              </div>
              <div className="ing-card__body">
                <div className="ing-card__grid">
                  <CardField label="Брутто" value={dispG(r.amount_brutto_g)} ro={ro} onChange={(v) => onRowChange(id, { amount_brutto_g: v })} />
                  <CardField label="Нетто" value={dispG(r.amount_netto_g)} ro={ro} onChange={(v) => onRowChange(id, { amount_netto_g: v })} />
                  <CardField label="Выход" value={dispG(r.weight_in_dish_g)} ro={ro} onChange={(v) => onRowChange(id, { weight_in_dish_g: v })} />
                  <div className="ing-card__field">
                    <label className="field__label">Ед.</label>
                    {ro ? <span className="num">{r.unit || 'г'}</span>
                      : <NativeSelect value={r.unit || 'г'} options={UNITS} onChange={(e) => onRowChange(id, { unit: e.target.value })} />}
                  </div>
                </div>
                <div className="ing-card__derived derived-zone">
                  <span>% хол.: <b className="num">{r.cold_loss_pct == null ? '—' : g1(r.cold_loss_pct)}</b></span>
                  <span>% теп.: <b className="num"><HotLoss v={r.hot_loss_pct} /></b></span>
                </div>
              </div>
            </div>
          );
        })}
        {scaled && <p className="ing-table__scale-note t-caption text-dim"><Icon name="info" size={13} /> Показан масштаб ×{sc}. Снимите масштаб (×1), чтобы редактировать.</p>}
      </div>
    );
  }

  function CardField({ label, value, ro, onChange }) {
    return (
      <div className="ing-card__field">
        <label className="field__label">{label}</label>
        {ro ? <span className="num">{value == null ? '—' : g1(value)}</span>
          : <NumberInput plain value={value ?? ''} unit="г" step={5} min={0} onChange={onChange} />}
      </div>
    );
  }

  function IngredientsTable(props) {
    const { useBreakpoint } = hooks;
    const { isMobile } = useBreakpoint();
    if (!props.rows || props.rows.length === 0) return null;
    return isMobile ? <MobileCards {...props} /> : <DesktopTable {...props} />;
  }

export { IngredientsTable };
